/**
 * Admin DB-env switch — nguồn sự thật MODULE-LEVEL cho môi trường dữ liệu admin đang xem
 * (US-AIF.P0-0247-S03 · @UC9419). Song song với `getToken()` (auth.ts): api client TRUNG TÂM
 * (openapi-fetch, ui-kit) và các feature RAW-FETCH cùng ĐỌC store này để quyết định có đính header
 * `X-Admin-Db-Env: sandbox` hay không — MỘT nguồn duy nhất, tránh drift.
 *
 * BẤT BIẾN (khớp backend S01/S02 — @gate security):
 * - `real` là MẶC ĐỊNH fail-safe: khi env=real ⇒ KHÔNG đính header (server đọc DB CHÍNH).
 * - `sandbox` ⇒ đính đúng header `X-Admin-Db-Env: sandbox`. Server VẪN là chốt thật (role + read-only).
 * - Header name/value lấy từ @goppy/contracts (ADMIN_DB_ENV_HEADER/ADMIN_DB_ENV_SANDBOX_VALUE),
 *   KHÔNG magic-string, để admin web ↔ api không lệch.
 * - 🔴 US-AE-0321: env thuộc **VÒNG ĐỜI PHIÊN**. Lựa chọn persist ở localStorage để sống qua RELOAD
 *   *trong* một phiên, nhưng MỌI ranh giới phiên (logout · phiên chết · mở màn /login) phải gọi
 *   `resetDbEnvToReal()`. Nếu không, admin KẾ TIẾP (có thể là người khác trên cùng máy) vừa đăng
 *   nhập đã ở sandbox ⇒ **nhìn số demo mà tưởng số thật**.
 *
 * Store thuần in-memory + pub/sub để `authHeaders()`/`onRequest` đọc ĐỒNG BỘ, còn React
 * (`DbEnvProvider` qua `useSyncExternalStore`) phản ánh NGAY cả khi reset đến từ ngoài React.
 */
import {
  ADMIN_DB_ENV_DEFAULT,
  ADMIN_DB_ENV_HEADER,
  ADMIN_DB_ENV_SANDBOX_VALUE,
  type AdminDbEnv,
} from "@goppy/contracts";

/** Khoá localStorage giữ lựa chọn env (persist qua reload TRONG một phiên). */
export const DB_ENV_STORAGE_KEY = "goppy.admin.dbEnv";

let currentEnv: AdminDbEnv = ADMIN_DB_ENV_DEFAULT;

/** Subscriber (React `useSyncExternalStore`) — mọi thay đổi env đều phát tín hiệu. */
const listeners = new Set<() => void>();

/** Đọc env dữ liệu admin hiện tại. `real` (mặc định) hoặc `sandbox`. */
export function currentDbEnv(): AdminDbEnv {
  return currentEnv;
}

/**
 * Đăng ký nhận thông báo khi env đổi. Trả hàm huỷ đăng ký.
 * Dùng cho `useSyncExternalStore` — nhờ đó reset từ auth.ts (ngoài React) làm banner ẩn NGAY.
 */
export function subscribeDbEnv(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Cập nhật store (DbEnvProvider gọi khi user đổi toggle + lúc khôi phục từ localStorage).
 * KHÔNG persist ở đây — persist là quyết định riêng (`persistDbEnv`), để hydrate/reset không ghi đè.
 */
export function setCurrentDbEnv(env: AdminDbEnv): void {
  if (currentEnv === env) return;
  currentEnv = env;
  for (const listener of listeners) listener();
}

/** Ghi lựa chọn env để sống qua reload. Lỗi ghi (private mode) ⇒ bỏ qua, store in-memory vẫn đúng. */
export function persistDbEnv(env: AdminDbEnv): void {
  try {
    localStorage.setItem(DB_ENV_STORAGE_KEY, env);
  } catch {
    // Bỏ qua: mất persist chỉ ảnh hưởng lần reload sau, KHÔNG ảnh hưởng env của phiên hiện tại.
  }
}

/** Đọc lựa chọn đã lưu. Giá trị hợp lệ duy nhất kích hoạt sandbox là "sandbox"; còn lại ⇒ real. */
export function readPersistedDbEnv(): AdminDbEnv {
  try {
    return localStorage.getItem(DB_ENV_STORAGE_KEY) === ADMIN_DB_ENV_SANDBOX_VALUE
      ? "sandbox"
      : ADMIN_DB_ENV_DEFAULT;
  } catch {
    // localStorage không khả dụng (private mode…) ⇒ fail-safe real.
    return ADMIN_DB_ENV_DEFAULT;
  }
}

/**
 * 🔴 US-AE-0321 (@UC9419) — ĐƯỜNG DUY NHẤT kéo admin về DB chính ở ranh giới phiên (logout · phiên
 * chết · mở màn /login). Xoá cả lựa chọn đã lưu (không sống sang phiên sau) + báo subscriber.
 * Idempotent: gọi khi đang ở `real` vẫn xoá lựa chọn đã lưu (fail-safe, không nửa vời).
 */
export function resetDbEnvToReal(): void {
  try {
    localStorage.removeItem(DB_ENV_STORAGE_KEY);
  } catch {
    // Bỏ qua lỗi xoá: store in-memory dưới đây mới là thứ quyết định header của phiên hiện tại.
  }
  setCurrentDbEnv(ADMIN_DB_ENV_DEFAULT);
}

/**
 * Header đính vào MỌI request (central client + raw fetch) theo env hiện tại.
 * - env=sandbox ⇒ `{ "x-admin-db-env": "sandbox" }` (HTTP header case-insensitive; khớp backend).
 * - env=real    ⇒ `{}` (KHÔNG đính header — fail-safe đọc DB chính).
 */
export function dbEnvHeaders(): Record<string, string> {
  return currentEnv === "sandbox" ? { [ADMIN_DB_ENV_HEADER]: ADMIN_DB_ENV_SANDBOX_VALUE } : {};
}
